import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { EASE_OUT } from '@/lib/motion';
import {
  useNotifications,
  deleteNotification,
  markNotificationRead,
  useMarkAllNotificationsRead,
} from '../../services/notifications';
import type { Notification } from '../../types/notification';
import NotificationItem from '../notifications/NotificationItem';
import GroupedChatNotificationItem from '../notifications/GroupedChatNotificationItem';

interface NotificationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Grouping helpers
// ---------------------------------------------------------------------------

type NotificationGroup =
  | { kind: 'single'; notification: Notification }
  | { kind: 'chat_group'; sender: string; notifications: Notification[]; latest: Notification };

function groupNotifications(notifications: Notification[]): NotificationGroup[] {
  const groups: NotificationGroup[] = [];
  const chatBySender = new Map<string, Notification[]>();

  for (const n of notifications) {
    if (n.type === 'chat_message') {
      const existing = chatBySender.get(n.title) ?? [];
      existing.push(n);
      chatBySender.set(n.title, existing);
    } else {
      groups.push({ kind: 'single', notification: n });
    }
  }

  for (const [sender, notifs] of chatBySender) {
    groups.push({ kind: 'chat_group', sender, notifications: notifs, latest: notifs[0] });
  }

  return groups.sort((a, b) => {
    const aDate = a.kind === 'single' ? a.notification.created_at : a.latest.created_at;
    const bDate = b.kind === 'single' ? b.notification.created_at : b.latest.created_at;
    return new Date(bDate).getTime() - new Date(aDate).getTime();
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({ isOpen, onClose }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isBulkReading, setIsBulkReading] = useState(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const markAllRead = useMarkAllNotificationsRead();

  const { data, isLoading, isError, refetch } = useNotifications();

  const notifications: Notification[] = data?.notifications ?? [];
  const unreadCount = notifications.filter((n) => !n.read).length;
  const allSelected =
    notifications.length > 0 && notifications.every((n) => selectedIds.has(n.id));

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleRead = (notification: Notification) => {
    if (!notification.read) {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
    if (notification.action_url) {
      onClose();
      navigate(notification.action_url);
    }
  };

  const handleDelete = async (ids: string[]) => {
    setDeletingIds((prev) => new Set([...prev, ...ids]));
    try {
      await Promise.all(ids.map((id) => deleteNotification(id)));
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  const handleToggleSelect = (ids: string[]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = ids.every((id) => next.has(id));
      if (allSelected) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleBulkDelete = async () => {
    setIsBulkDeleting(true);
    const ids = Array.from(selectedIds);
    try {
      await Promise.all(ids.map((id) => deleteNotification(id)));
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    } finally {
      setIsBulkDeleting(false);
      setSelectedIds(new Set());
      setIsEditMode(false);
    }
  };

  const handleBulkMarkRead = async () => {
    setIsBulkReading(true);
    const ids = Array.from(selectedIds).filter(
      (id) => !notifications.find((n) => n.id === id)?.read
    );
    try {
      await Promise.all(ids.map((id) => markNotificationRead(id)));
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    } finally {
      setIsBulkReading(false);
      setSelectedIds(new Set());
      setIsEditMode(false);
    }
  };

  const handleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(notifications.map((n) => n.id)));
  };

  // Mark a single notification (or a chat group) read in place — no
  // navigation, just refresh the list.
  const handleMarkRead = async (id: string) => {
    await markNotificationRead(id).catch(() => {});
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  // -------------------------------------------------------------------------
  // Derived list — one list, no All/Unread split (everything stays visible;
  // read items just render dimmed).
  // -------------------------------------------------------------------------

  const groups = groupNotifications(notifications);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: EASE_OUT }}
            className="fixed inset-0 bg-black/50 z-40"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.3, ease: EASE_OUT }}
            className="fixed top-0 right-0 h-full w-full max-w-md bg-surface-primary dark:bg-surface-primary shadow-xl z-50 overflow-hidden flex flex-col"
            data-testid="notifications-panel"
          >
            {/* Header */}
            <div className="p-6 border-b border-border-primary dark:border-border-primary">
              <div className="flex items-center justify-between mb-4">
                <h2 className="heading-3">Notifications</h2>
                <div className="flex items-center gap-2">
                  {notifications.length > 0 && (
                    <button
                      onClick={() => {
                        setIsEditMode((e) => !e);
                        setSelectedIds(new Set());
                      }}
                      className="text-sm font-medium text-primary-500 hover:text-primary-700 dark:hover:text-primary-300 transition-colors px-2 py-1"
                      data-testid="notifications-panel-edit-toggle"
                    >
                      {isEditMode ? 'Done' : 'Edit'}
                    </button>
                  )}
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-background-secondary dark:hover:bg-background-tertiary rounded-full transition-colors"
                    aria-label="Close notifications"
                    data-testid="notifications-panel-close"
                  >
                    <XMarkIcon className="w-5 h-5 text-text-secondary" />
                  </button>
                </div>
              </div>

              {/* Quick actions — select-all in edit mode, mark-all-read otherwise */}
              {isEditMode ? (
                <button
                  onClick={handleSelectAll}
                  className="text-sm font-medium text-primary-500 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                  data-testid="notifications-panel-select-all"
                >
                  {allSelected ? 'Deselect all' : 'Select all'}
                </button>
              ) : (
                unreadCount > 0 && (
                  <button
                    onClick={() => markAllRead.mutate()}
                    disabled={markAllRead.isPending}
                    className="text-sm font-medium text-primary-500 hover:text-primary-700 dark:hover:text-primary-300 transition-colors disabled:opacity-50"
                    data-testid="notifications-panel-mark-all-read"
                  >
                    {markAllRead.isPending ? 'Marking…' : `Mark all as read (${unreadCount})`}
                  </button>
                )
              )}
            </div>

            {/* Notifications List */}
            <div className="flex-1 overflow-y-auto" data-testid="notifications-panel-list">
              {isLoading ? (
                <div
                  className="flex items-center justify-center py-16 text-text-secondary body-small"
                  data-testid="notifications-panel-loading"
                >
                  Loading notifications…
                </div>
              ) : isError ? (
                <div
                  className="flex flex-col items-center justify-center gap-3 py-16 px-6 text-center"
                  data-testid="notifications-panel-error"
                >
                  <p className="body-small text-text-secondary">
                    We couldn&apos;t load your notifications.
                  </p>
                  <button onClick={() => refetch()} className="btn-secondary py-1.5 px-4">
                    Try again
                  </button>
                </div>
              ) : groups.length === 0 ? (
                <div
                  className="flex items-center justify-center py-16 text-text-secondary body-small"
                  data-testid="notifications-panel-empty"
                >
                  You&apos;re all caught up.
                </div>
              ) : (
                groups.map((group) => {
                  if (group.kind === 'single') {
                    return (
                      <NotificationItem
                        key={group.notification.id}
                        notification={group.notification}
                        onRead={() => handleRead(group.notification)}
                        onMarkRead={handleMarkRead}
                        onDelete={(id) => handleDelete([id])}
                        isDeleting={deletingIds.has(group.notification.id)}
                        isEditMode={isEditMode}
                        isSelected={selectedIds.has(group.notification.id)}
                        onToggleSelect={(id) => handleToggleSelect([id])}
                      />
                    );
                  }
                  return (
                    <GroupedChatNotificationItem
                      key={`chat-${group.sender}`}
                      sender={group.sender}
                      notifications={group.notifications}
                      latest={group.latest}
                      onMarkAllRead={() => {
                        if (group.notifications.some((n) => !n.read)) {
                          queryClient.invalidateQueries({ queryKey: ['notifications'] });
                        }
                        if (group.latest.action_url) {
                          onClose();
                          navigate(group.latest.action_url);
                        }
                      }}
                      onMarked={() =>
                        queryClient.invalidateQueries({ queryKey: ['notifications'] })
                      }
                      onDelete={(ids) => handleDelete(ids)}
                      isDeleting={group.notifications.some((n) => deletingIds.has(n.id))}
                      isEditMode={isEditMode}
                      isSelected={group.notifications.every((n) => selectedIds.has(n.id))}
                      onToggleSelect={(ids) => handleToggleSelect(ids)}
                    />
                  );
                })
              )}
            </div>

            {/* Bulk actions footer */}
            {isEditMode && selectedIds.size > 0 && (
              <div className="p-4 border-t border-border-primary dark:border-border-primary flex-shrink-0 flex gap-2">
                <button
                  onClick={handleBulkMarkRead}
                  disabled={isBulkReading || isBulkDeleting}
                  className="flex-1 py-2.5 px-4 rounded-full text-sm font-medium transition-colors flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="notifications-panel-bulk-read"
                >
                  {isBulkReading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      Marking…
                    </>
                  ) : (
                    `Mark as read (${selectedIds.size})`
                  )}
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={isBulkDeleting || isBulkReading}
                  className={`flex-1 py-2.5 px-4 rounded-full text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    isBulkDeleting
                      ? 'bg-error-100 text-error-400 cursor-not-allowed'
                      : 'bg-error-600 text-white hover:bg-error-700 active:scale-[0.97] disabled:opacity-50'
                  }`}
                  data-testid="notifications-panel-bulk-delete"
                >
                  {isBulkDeleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      Deleting…
                    </>
                  ) : (
                    `Delete (${selectedIds.size})`
                  )}
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NotificationsPanel;
